import type { Tag, Transaction, VATRate } from '@finapp/types';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { apiUrl } from '../config/api';
import { vatService } from '../services/vatService';
import { useAuth } from '../contexts/AuthContext';
import { TagSelector } from './TagSelector';
import { CostpointAssignmentList } from './transactions/CostpointAssignmentList';

interface TransactionEditModalProps {
  transaction: Transaction | null;
  onClose: () => void;
  onSave: (transaction: Partial<Transaction>) => Promise<void>;
  onTransactionUpdate?: (transaction: Transaction) => void;
  onRefresh?: () => void; // New prop to trigger list refresh
  isOpen: boolean;
}

export const TransactionEditModal: React.FC<TransactionEditModalProps> = ({
  transaction,
  onClose,
  onSave,
  onTransactionUpdate,
  isOpen,
  onRefresh,
}) => {
  const [formData, setFormData] = useState({
    date: '',
    heading: '',
    description: '',
    type: 'deposit' as 'deposit' | 'payment',
    has_vat: false,
    vat_id: null as number | null,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [tags, setTags] = useState<Tag[]>([]);
  const [vatRates, setVatRates] = useState<VATRate[]>([]);
  const [loadingVatRates, setLoadingVatRates] = useState(false);
  const [costpointTags, setCostpointTags] = useState<Record<number, Tag[]>>({});
  const [availableCostpoints, setAvailableCostpoints] = useState<string[]>([]);
  const { user } = useAuth();

  // Check if user can edit costpoints (admin, superadmin, or operator)
  const canEditCostpoints =
    user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'operator';
  // Fetch VAT rates
  const fetchVatRates = useCallback(async () => {
    try {
      setLoadingVatRates(true);
      const rates = await vatService.getVatRates();
      console.log('🔍 [TransactionEditModal] VAT rates fetched:', rates);
      setVatRates(rates);
    } catch (error) {
      console.error('Failed to fetch VAT rates:', error);
      // Don't show error toast for now to avoid test failures
    } finally {
      setLoadingVatRates(false);
    }
  }, []);

  // Fetch available costpoints
  const fetchCostpoints = useCallback(async () => {
    try {
      const response = await fetch(apiUrl('costpoints'), {
        credentials: 'include',
      });
      if (response.ok) {
        const costpoints = await response.json();
        setAvailableCostpoints(costpoints.map((cp: { name: string }) => cp.name));
      }
    } catch (error) {
      console.error('Failed to fetch costpoints:', error);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchVatRates();
      if (canEditCostpoints) {
        fetchCostpoints();
      }
    }
  }, [isOpen, canEditCostpoints, fetchCostpoints, fetchVatRates]);

  useEffect(() => {
    if (transaction) {
      // Format date for input field (YYYY-MM-DD)
      const date = new Date(transaction.date);
      const formattedDate = date.toISOString().split('T')[0] || '';

      setFormData({
        date: formattedDate,
        heading: transaction.heading || '',
        description: transaction.description || '',
        type: transaction.type,
        has_vat: transaction.has_vat || false,
        vat_id: transaction.vat_id,
      });
      setTags(transaction.tags || []);

      // Initialize costpoint tags
      const initialCostpointTags: Record<number, Tag[]> = {};
      transaction.costpoints?.forEach((costpoint) => {
        initialCostpointTags[costpoint.id] = costpoint.tags || [];
      });
      setCostpointTags(initialCostpointTags);

      setErrors({});
    }
  }, [transaction]);

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    if (!formData.heading.trim()) {
      newErrors.heading = 'Heading is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Autosave function — uses overrideData when provided, otherwise falls back to current form state
  const autoSave = useCallback(
    async (overrideData?: Partial<typeof formData>) => {
      const dataToSave = overrideData ? { ...formData, ...overrideData } : formData;
      console.log('🔄 Autosave triggered with formData:', dataToSave);
      if (!transaction) {
        console.log('❌ Autosave skipped - no transaction');
        return;
      }
      // Only validate on debounced saves; explicit field saves (overrideData) skip validation
      // to match original autoSaveWithData behaviour which never called validateForm()
      if (!overrideData && !validateForm()) {
        console.log('❌ Autosave skipped - validation failed');
        return;
      }

      setSaving(true);
      try {
        const updatedTransaction: Partial<Transaction> = {
          date: dataToSave.date,
          heading: dataToSave.heading.trim(),
          type: dataToSave.type,
          has_vat: Boolean(dataToSave.has_vat), // Ensure boolean type
          vat_id: dataToSave.vat_id,
        };

        // Only include description if it's not empty (backend validation requires non-empty if provided)
        if (dataToSave.description?.trim()) {
          updatedTransaction.description = dataToSave.description.trim();
        }

        console.log('Sending transaction update:', updatedTransaction);

        await onSave(updatedTransaction);
        toast.success('Transaction saved automatically', {
          position: 'bottom-right',
          autoClose: 3000,
          hideProgressBar: true,
        });
      } catch (error) {
        console.error('Failed to save transaction:', error);

        // Show permission-specific error for 403
        const status =
          error instanceof Error && 'status' in error
            ? (error as { status: number }).status
            : undefined;
        if (status === 403) {
          toast.error('No permission to update transactions', {
            position: 'bottom-right',
            autoClose: 4000,
            hideProgressBar: true,
          });
        } else {
          toast.error('Failed to save transaction', {
            position: 'bottom-right',
            autoClose: 4000,
            hideProgressBar: true,
          });
        }
      } finally {
        setSaving(false);
      }
    },
    [formData, transaction, validateForm, onSave]
  );

  // Debounced autosave - only save if form data actually changed
  useEffect(() => {
    if (!transaction) return;

    // Don't autosave on initial load (exclude VAT fields to allow VAT changes to trigger autosave)
    const isInitialLoad =
      formData.date === new Date(transaction.date).toISOString().split('T')[0] &&
      new Date(transaction.date).toISOString().split('T')[0] &&
      formData.heading === (transaction.heading || '') &&
      formData.description === (transaction.description || '') &&
      formData.type === transaction.type;

    if (isInitialLoad) return;

    const timeoutId = setTimeout(() => {
      autoSave();
    }, 2000); // 2 second delay

    return () => clearTimeout(timeoutId);
  }, [formData, transaction, autoSave]);

  const handleTagsChange = async (newTags: Tag[]) => {
    if (!transaction) return;

    console.log('🔄 Updating tags for transaction', transaction.id, ':', newTags);
    console.log('📊 Previous tags:', tags);
    console.log('📊 New tags:', newTags);
    setSaving(true);
    try {
      const tagIds = newTags.map((tag) => tag.id);
      console.log('📤 Sending tag IDs:', tagIds);

      const response = await fetch(apiUrl(`transactions/${transaction.id}/tags`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ tagIds }),
      });

      console.log('📥 Response status:', response.status);

      if (response.ok) {
        const updatedTransaction = await response.json();
        console.log('✅ Tags updated successfully:', updatedTransaction);
        setTags(newTags);

        // Update the transaction in the parent component
        if (onTransactionUpdate) {
          onTransactionUpdate(updatedTransaction);
        }

        toast.success('Tags updated successfully', {
          position: 'bottom-right',
          autoClose: 3000,
          hideProgressBar: true,
        });
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to update tags:', response.status, errorText);

        // Show permission-specific error for 403
        if (response.status === 403) {
          toast.error('No permission to update transactions', {
            position: 'bottom-right',
            autoClose: 4000,
          });
        } else {
          toast.error('Failed to update tags', {
            position: 'bottom-right',
            autoClose: 4000,
          });
        }
      }
    } catch (error) {
      console.error('❌ Failed to update tags:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCostpointTagChange = async (costpointId: number, newTags: Tag[]) => {
    if (!transaction) return;

    console.log('🔄 Updating costpoint tags for costpoint', costpointId, ':', newTags);

    try {
      const tagIds = newTags.map((tag) => tag.id);
      console.log('📤 Sending costpoint tag IDs:', tagIds);

      const response = await fetch(
        apiUrl(`transactions/${transaction.id}/costpoints/${costpointId}/tags`),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ tagIds }),
        }
      );

      console.log('📥 Costpoint tags response status:', response.status);

      if (response.ok) {
        const updatedTransaction = await response.json();
        console.log('✅ Costpoint tags updated successfully:', updatedTransaction);

        // Update local state
        setCostpointTags((prev) => ({
          ...prev,
          [costpointId]: newTags,
        }));

        // Update the transaction in the parent component
        if (onTransactionUpdate) {
          onTransactionUpdate(updatedTransaction);
        }

        toast.success('Costpoint tags updated successfully', {
          position: 'bottom-right',
          autoClose: 3000,
          hideProgressBar: true,
        });
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to update costpoint tags:', response.status, errorText);

        // Show permission-specific error for 403
        if (response.status === 403) {
          toast.error('No permission to update transactions', {
            position: 'bottom-right',
            autoClose: 4000,
          });
        } else {
          toast.error('Failed to update costpoint tags', {
            position: 'bottom-right',
            autoClose: 4000,
          });
        }
      }
    } catch (error) {
      console.error('❌ Failed to update costpoint tags:', error);
      toast.error('Failed to update costpoint tags', {
        position: 'bottom-right',
        autoClose: 4000,
      });
    }
  };

  const handleCostpointChange = async (costpointAssignmentId: number, newCostpoint: string) => {
    if (!transaction) return;

    console.log('🔄 Changing costpoint for assignment', costpointAssignmentId, 'to', newCostpoint);

    try {
      const response = await fetch(
        apiUrl(`transactions/${transaction.id}/costpoints/${costpointAssignmentId}`),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ costpoint: newCostpoint }),
        }
      );

      console.log('📥 Costpoint change response status:', response.status);

      if (response.ok) {
        const updatedTransaction = await response.json();
        console.log('✅ Costpoint changed successfully:', updatedTransaction);

        // Update the transaction in the parent component
        if (onTransactionUpdate) {
          onTransactionUpdate(updatedTransaction);
        }

        toast.success('Costpoint updated successfully', {
          position: 'bottom-right',
          autoClose: 3000,
          hideProgressBar: true,
        });
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to change costpoint:', response.status, errorText);
        toast.error('Failed to update costpoint', {
          position: 'bottom-right',
          autoClose: 4000,
        });
      }
    } catch (error) {
      console.error('❌ Failed to change costpoint:', error);
      toast.error('Failed to update costpoint', {
        position: 'bottom-right',
        autoClose: 4000,
      });
    }
  };

  const handleCostpointAmountChange = async (costpointAssignmentId: number, newAmount: number) => {
    if (!transaction) return;

    try {
      const response = await fetch(
        apiUrl(`transactions/${transaction.id}/costpoints/${costpointAssignmentId}`),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            costpoint: transaction.costpoints?.find((cp) => cp.id === costpointAssignmentId)
              ?.costpoint,
            amount: newAmount,
          }),
        }
      );

      if (response.ok) {
        const updatedTransaction = await response.json();
        // Update parent
        if (onTransactionUpdate) {
          onTransactionUpdate(updatedTransaction);
        }
        toast.success('Amount updated');
      } else {
        toast.error('Failed to update amount');
      }
    } catch (error) {
      console.error('Failed to update costpoint amount:', error);
      toast.error('Failed to update amount');
    }
  };

  const handleCostpointVatChange = async (costpointAssignmentId: number, newVatId: number) => {
    if (!transaction) return;

    // Check if this is truly a change
    if (transaction.vat_id === newVatId) {
      return;
    }

    if (
      !window.confirm(
        'Changing the VAT rate for this assignment will move it to a separate transaction. Continue?'
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        apiUrl(`transactions/${transaction.id}/costpoints/${costpointAssignmentId}`),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            costpoint: transaction.costpoints?.find((cp) => cp.id === costpointAssignmentId)
              ?.costpoint,
            vat_id: newVatId,
          }),
        }
      );

      if (response.ok) {
        toast.success('Assignment updated and moved to new transaction', {
          position: 'bottom-right',
          autoClose: 3000,
          hideProgressBar: true,
        });

        // Refresh the current transaction because the assignment has likely moved
        const refreshResponse = await fetch(apiUrl(`transactions/${transaction.id}`), {
          credentials: 'include',
        });

        if (refreshResponse.ok) {
          const refreshed = await refreshResponse.json();
          // Force local update if possible, but parent update is key
          if (onTransactionUpdate) {
            console.log('Calling onTransactionUpdate with refreshed data');
            onTransactionUpdate(refreshed);
          }
        }

        // Trigger full list refresh if available (to show the new split transaction)
        if (onRefresh) {
          onRefresh();
        }

        // Close the modal as requested by user
        onClose();
      } else {
        toast.error('Failed to update costpoint VAT', {
          position: 'bottom-right',
          autoClose: 4000,
        });
      }
    } catch (error) {
      console.error('❌ Failed to change costpoint VAT:', error);
      toast.error('Failed to update costpoint VAT', {
        position: 'bottom-right',
        autoClose: 4000,
      });
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  if (!isOpen || !transaction) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center"
      data-testid="transaction-edit-modal-overlay"
    >
      <div
        className="relative top-4 sm:top-20 p-4 sm:p-5 border w-full max-w-md sm:max-w-2xl lg:max-w-4xl shadow-lg rounded-md bg-white m-4 sm:m-0"
        role="dialog"
        data-testid="transaction-edit-modal"
      >
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Edit Transaction</h3>
            <button
              onClick={handleClose}
              disabled={saving}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              data-testid="transaction-edit-modal-close"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6">
            {/* Left Column - Basic Transaction Info */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 border-b pb-2">
                Transaction Details
              </h4>

              {/* Date Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                    Date
                  </label>
                  <input
                    type="date"
                    id="date"
                    value={formData.date}
                    onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                      errors.date ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                    }`}
                    disabled={saving}
                  />
                  {errors.date && <p className="mt-1 text-sm text-red-600">{errors.date}</p>}
                </div>

                {transaction.original_date && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Original Date</label>
                    <div className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                      {new Date(transaction.original_date).toLocaleDateString()}
                    </div>
                  </div>
                )}
              </div>

              {/* Heading Fields */}
              <div className="space-y-2">
                {transaction.original_heading && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Original Heading
                    </label>
                    <div className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                      {transaction.original_heading}
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="heading" className="block text-sm font-medium text-gray-700">
                    Heading <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="heading"
                    value={formData.heading}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        heading: e.target.value,
                      }))
                    }
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                      errors.heading ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                    }`}
                    placeholder="Enter transaction heading/title"
                    disabled={saving}
                  />
                  {errors.heading && <p className="mt-1 text-sm text-red-600">{errors.heading}</p>}
                </div>
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description (optional)
                </label>
                <textarea
                  id="description"
                  data-testid="transaction-edit-description-textarea"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="Add your notes or comments about this transaction"
                  rows={2}
                  disabled={saving}
                />
              </div>

              {/* Amount and Type */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount (€)</label>
                  <div className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                    <span
                      className={
                        transaction?.type === 'deposit' ? 'text-green-600' : 'text-red-600'
                      }
                    >
                      {transaction?.type === 'deposit' ? '+' : '-'}
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'EUR',
                        minimumFractionDigits: 2,
                      }).format(Math.abs(transaction?.amount || 0))}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Bank statement data</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Type</label>
                  <div className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                    <span
                      className={
                        transaction?.type === 'deposit' ? 'text-green-600' : 'text-red-600'
                      }
                    >
                      {transaction?.type === 'deposit' ? 'Income' : 'Expense'}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Bank statement data</p>
                </div>
              </div>

              {/* VAT */}
              <div>
                <label className="block text-sm font-medium text-gray-700">VAT</label>
                <div className="mt-1 space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="has_vat"
                      data-testid="transaction-edit-vat-checkbox"
                      checked={formData.has_vat}
                      onChange={(e) => {
                        const newHasVat = e.target.checked;
                        const newVatId = newHasVat ? formData.vat_id : null;
                        console.log('VAT checkbox changed:', newHasVat);
                        setFormData((prev) => ({
                          ...prev,
                          has_vat: newHasVat,
                          vat_id: newHasVat ? prev.vat_id : null,
                        }));
                        // Pass explicit overrideData so autoSave uses the new values rather than
                        // a stale closure of formData, and so validateForm() is bypassed (same
                        // pattern as the VAT rate select below).
                        setTimeout(() => autoSave({ has_vat: newHasVat, vat_id: newVatId }), 100);
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      disabled={saving}
                    />
                    <label htmlFor="has_vat" className="ml-2 text-sm text-gray-700">
                      This transaction includes VAT
                    </label>
                  </div>

                  {formData.has_vat && (
                    <div>
                      <label htmlFor="vat_id" className="block text-sm font-medium text-gray-700">
                        VAT Rate
                      </label>
                      {loadingVatRates ? (
                        <div className="mt-1 text-sm text-gray-500">Loading VAT rates...</div>
                      ) : (
                        <select
                          id="vat_id"
                          data-testid="transaction-edit-vat-rate-select"
                          value={formData.vat_id || ''}
                          onChange={(e) => {
                            const newVatId = e.target.value ? parseInt(e.target.value, 10) : null;
                            setFormData((prev) => ({
                              ...prev,
                              vat_id: newVatId,
                            }));
                            setTimeout(() => {
                              autoSave({ vat_id: newVatId, has_vat: true });
                            }, 500);
                          }}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          disabled={saving}
                        >
                          <option value="">Select VAT rate...</option>
                          {vatRates.map((rate) => (
                            <option key={rate.id} value={rate.id}>
                              {rate.name} ({rate.rate}%)
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Transaction Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Transaction Tags</label>
                <div className="mt-1">
                  <TagSelector
                    selectedTags={tags}
                    onTagsChange={handleTagsChange}
                    placeholder="Add tags to categorize this transaction..."
                    maxTags={10}
                    data-testid="transaction-edit-tag-selector"
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Costpoint Assignments */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 border-b pb-2">
                Costpoint Assignments
                <span className="text-xs text-gray-500 ml-2">
                  ({transaction?.costpoints?.length || 0} assigned)
                </span>
              </h4>

              <CostpointAssignmentList
                costpointAssignments={transaction?.costpoints || []}
                costpointTags={costpointTags}
                availableCostpoints={availableCostpoints}
                vatRates={vatRates}
                transactionVatId={transaction.vat_id}
                transactionDate={transaction.date}
                canEditCostpoints={canEditCostpoints}
                isSuperAdmin={user?.role === 'superadmin'}
                onTagChange={handleCostpointTagChange}
                onVatChange={handleCostpointVatChange}
                onCostpointChange={handleCostpointChange}
                onAmountChange={handleCostpointAmountChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
